/**
 * Slice 50 — owner's job detail: what the cleaner ticked (and photographed) under "When you arrive".
 * Wording comes from the copy taken when the job started, so later edits to the list do not rewrite old jobs.
 */
import { Check } from "lucide-react";
import { cn } from "@/lib/utils";
import { SimplePhotoGrid } from "@/components/owner/SubmissionPhotoGallery";
import { isArrivalTaskDone, parseArrivalTasks, readArrivalAnswers } from "@/lib/arrivalChecklist";
import { readSimpleFlowState } from "@/lib/simpleTurnover";

export function ArrivalAnswersSection({ arrivalTasks, entryWalkthroughState }: { arrivalTasks: unknown; entryWalkthroughState: unknown }) {
  const tasks = parseArrivalTasks(arrivalTasks) ?? [];
  if (tasks.length === 0) return null;
  const answers = readArrivalAnswers(readSimpleFlowState(entryWalkthroughState));
  const done = tasks.filter((t) => isArrivalTaskDone(t, answers[t.id])).length;

  return (
    <div className="flex flex-col gap-3" data-testid="arrival-answers">
      <div className="flex items-baseline justify-between gap-3">
        <div className="text-xs font-bold tracking-[0.8px] text-[#64748b]">WHEN YOU ARRIVE</div>
        <div className="text-[14px] font-bold text-[#15776d]">
          {done} of {tasks.length} done
        </div>
      </div>
      <div className="flex flex-col gap-2.5">
        {tasks.map((task) => {
          const answer = answers[task.id];
          const ticked = isArrivalTaskDone(task, answer);
          const photos = answer?.photo_urls ?? [];
          return (
            <div key={task.id} className="flex flex-col gap-2.5 rounded-[14px] border-[1.5px] border-[#e6edf1] bg-white px-3.5 py-3">
              <div className="flex items-center gap-3">
                <div
                  className={cn(
                    "flex h-8 w-8 shrink-0 items-center justify-center rounded-[9px]",
                    ticked ? "bg-[#24a89a]" : "border-2 border-[#cdd8df]",
                  )}
                >
                  {ticked ? <Check className="h-[18px] w-[18px] text-white" strokeWidth={3.2} /> : null}
                </div>
                <div className="min-w-0 flex-1">
                  <div className="text-[15px] font-bold text-[#0f1720]">{task.text}</div>
                  <div className="text-[12.5px] font-bold text-[#8494a2]">
                    {task.required ? "Required" : "Optional"}
                    {!ticked ? " · not done" : ""}
                  </div>
                </div>
              </div>
              {photos.length > 0 ? (
                <SimplePhotoGrid photos={photos.map((photo_url, i) => ({ id: `${task.id}-${i}`, photo_url }))} />
              ) : null}
            </div>
          );
        })}
      </div>
    </div>
  );
}
